import type { NavigateToOptions } from '#app/composables/router'
import type { LocationQueryValue } from "#vue-router";
import { safeInternalPath } from "@/utils/redirect";
import type { UserSelf } from "@/utils/types";

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null as UserSelf | null,
    authRedirect: null as string | null,
  }),
  actions: {
    setAuthRedirect(redirect?: LocationQueryValue|LocationQueryValue[]) {
      if (Array.isArray(redirect)) {
        redirect = redirect[0];
      }

      const safe = safeInternalPath(redirect);
      if (safe) {
        this.authRedirect = safe;
      }
    },
    clearAuthRedirect() {
      this.authRedirect = null;
    }
  },
  persist: {
    storage: sessionStorage,
    pick: ['authRedirect'],
  }
})

export function useAuth() {
  const store = useAuthStore();
  const user = computed(() => store.user);
  const loggedIn = computed(() => !!store.user);

  async function fetchUser() {
    try {
      store.user = await $fetch<UserSelf>('/api/v1/users/self/', {
        method: 'GET',
      });
    } catch {
      store.user = null;
    }
    return store.user;
  }

  async function redirect(to?: LocationQueryValue|LocationQueryValue[]) {
    const fallback = store.user?.is_defender
      ? '/leakprofiles/'
      : store.user?.is_attacker
        ? '/search/'
        : '/';
    const stored = store.authRedirect;
    store.authRedirect = null;

    const explicit = Array.isArray(to) ? to[0] : to;
    const target = safeInternalPath(explicit) ?? safeInternalPath(stored) ?? fallback;
    const external = ['/api', '/admin', '/static'].some(p => target.startsWith(p));
    try {
      return await navigateTo(target, { external });
    } catch (error) {
      if (target === fallback) {
        throw error;
      }
      return await navigateTo(fallback);
    }
  }

  async function redirectToReAuth(options?: NavigateToOptions) {
    const route = useRoute();
    store.setAuthRedirect(route.fullPath);
    return await navigateTo('/login/reauth/', options);
  }

  async function logout() {
    try {
      await $fetch('/api/v1/auth/logout/', {
        method: 'POST',
        body: {}, // Send request as JSON to prevent CSRF errors
      });
    } catch {
      // Ignore errors
    }
    await navigateTo('/login/');
    store.user = null;
  }

  return {
    loggedIn,
    user,
    store,
    logout,
    redirect,
    redirectToReAuth,
    fetchUser,
  };
}

export function useCanCreateApiToken() {
  const auth = useAuth();
  return computed(() =>
    Boolean(auth.user.value?.is_superuser)
    || Boolean(auth.user.value?.is_attacker_paid)
    || Boolean(auth.user.value?.is_defender_paid),
  );
}
